<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <ais-instant-search
    class="searchResults"
    :search-client="searchClient"
    :index-name="indexPrefix + '_Villas'"
  >
    <ais-configure
      :query="query"
      :hits-per-page.camel="4"
      :distinct="true"
      :analytics="false"
      :enable-personalization.camel="true"
      attributesToRetrieve="objectID"
      attributesToHighlight=""
      :clickAnalytics="true"
    />

    <!-- SHOW ONLY WHEN NO RESULTS -->
    <ais-state-results
      v-show="
        counts.villas +
          counts.collections +
          counts.experiences +
          counts.cmspages ===
          0
      "
      style="display: contents"
    >
      <slot
        class="searchResults--section noResults"
      >
        <div>
          <p>
            Sorry, we couldn't find any matching result for
            <span class="bold">'{{ query }}'</span>
          </p>
          <h3>How about searching these collections</h3>
          <ul>
            <li>
              <app-link id="519" type="collection" class="link-unstyle">
                Think Exquisite villas
              </app-link>
            </li>
            <li>
              <app-link id="518" type="collection" class="link-unstyle">
                Villas with sea views
              </app-link>
            </li>
            <li>
              <app-link id="447" type="collection" class="link-unstyle">
                Contemporary design villas
              </app-link>
            </li>
            <li>
              <app-link id="554" type="collection" class="link-unstyle">
                La Dolce Vita villas in Italy
              </app-link>
            </li>
            <li>
              <app-link id="598" type="collection" class="link-unstyle">
                Perfect for families
              </app-link>
            </li>
            <li>
              <app-link id="529" type="collection" class="link-unstyle">
                Walking to shops and restaurants
              </app-link>
            </li>
            <li>
              <app-link id="88" type="collection" class="link-unstyle">
                Historical villas
              </app-link>
            </li>
          </ul>
        </div>
      </slot>
    </ais-state-results>

    <!-- Villas -->
    <ais-index v-show="counts.villas > 0" :index-name="indexPrefix + '_Villas'">
      <ais-configure
        :analytics="true"
        attributesToRetrieve="objectID,name,image,region,regionSub,sleeps,beds,baths"
      />
      <algolia-result-counter
        index-name="villas"
        @nbHitsChange="countUpdate"
      />

      <ais-hits>
        <div
          v-if="items.length > 0"
          slot-scope="{ items }"
          class="searchResults--section"
        >
          <div>
            <h3>Featured villas</h3>
            <ul>
              <li v-for="(item, index) in items" :key="item.objectID">
                <div
                  class="card villa--card"
                  :class="{ last: index == items.length - 1 }"
                  @click="
                    insightsClick({
                      queryID: item.__queryID,
                      indexName: indexPrefix + '_Villas',
                      objectID: item.objectID,
                      position: item.__position,
                    })
                  "
                >
                  <a
                    class="card__image villa"
                    :style="{
                      backgroundImage: `url(${cleanImageUrl(item.image)})`,
                    }"
                  >
                    <!-- if image is unavailable show logo -->
                    <img
                      :class="{ 'z-index' : item.image == ''}"
                      alt="TTT logo"
                      data-src="../../../assets/img/src/logos/monogram.svg"
                      width="94"
                      height="94"
                    >
                  </a>
                  <div :id="item.objectID" class="card__details">
                    <p class="card__details--region">
                      {{ item.regionSub }}, {{ item.region }}
                    </p>
                    <p class="card__details--name link-unstyle">
                      {{ item.name }}
                    </p>
                    <div class="card__details--stats">
                      <p>
                        {{ item.sleeps }} Guests
                      </p>
                      <p>
                        {{ item.beds }} Beds
                      </p>
                      <p>
                        {{ item.baths }} Bathrooms
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div class="text-center">
            <app-link id="16" type="page" class="btn-secondary-dark-sm">
              See all our villas
            </app-link>
          </div>
        </div>
      </ais-hits>
    </ais-index>

    <!-- Collections -->
    <ais-index
      v-show="counts.collections > 0"
      :index-name="indexPrefix + '_Collections'"
    >
      <ais-configure
        :analytics="true"
        attributesToRetrieve="objectID,name,image"
      />
      <algolia-result-counter
        index-name="collections"
        @nbHitsChange="countUpdate"
      />

      <ais-hits>
        <div
          v-if="items.length > 0"
          slot-scope="{ items }"
          class="searchResults--section"
        >
          <div>
            <h3>Featured collections</h3>
            <ul>
              <li v-for="(item, index) in items" :key="item.objectID">
                <div
                  class="card"
                  :class="{ last: index == items.length - 1 }"
                  @click="
                    insightsClick({
                      queryID: item.__queryID,
                      indexName: indexPrefix + '_Collections',
                      objectID: item.objectID,
                      position: item.__position,
                    })
                  "
                >
                  <a :id="item.objectID" type="collection">
                    <div
                      class="card__image"
                      style="background-size: cover"
                      :style="{
                        backgroundImage: `url(${cleanImageUrl(item.image)})`
                      }"
                    >
                      <!-- if image is unavailable show logo -->
                      <img
                        :class="{ 'z-index' : item.image == ''}"
                        alt="TTT logo"
                        src="../../../assets/img/src/logos/monogram.svg"
                        width="94"
                        height="94"
                      >
                    </div>
                  </a>

                  <div class="card__details">
                    <a
                      :id="item.objectID"
                      type="collection"
                    ><p
                      class="card__details--name link-unstyle text-dark"
                    >{{ item.name }}</p></a>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div class="text-center">
            <app-link id="1038" type="page" class="btn-secondary-dark-sm">
              See more collections
            </app-link>
          </div>
        </div>
      </ais-hits>
    </ais-index>

    <!--Experiences -->
    <ais-index
      v-show="counts.experiences > 0"
      :index-name="indexPrefix + '_CMSPages'"
    >
      <ais-configure
        :analytics="true"
        filters="thinkExperience:true"
        attributesToRetrieve="objectID,name,image"
      />
      <algolia-result-counter
        index-name="experiences"
        @nbHitsChange="countUpdate"
      />

      <ais-hits>
        <div
          v-if="items.length > 0"
          slot-scope="{ items }"
          class="searchResults--section"
        >
          <div>
            <h3>Featured Experiences</h3>
            <ul>
              <li v-for="(item, index) in items" :key="item.objectID">
                <div
                  class="card"
                  :class="{ last: index == items.length - 1 }"
                  @click="
                    insightsClick({
                      queryID: item.__queryID,
                      indexName: indexPrefix + '_CMSPages',
                      objectID: item.objectID,
                      position: item.__position,
                    })
                  "
                >
                  <a :id="item.objectID" type="page">
                    <div
                      class="card__image"
                      style="background-size: cover"
                      :style="{
                        backgroundImage: `url(${cleanImageUrl(item.image)})`,
                      }"
                    >
                      <!-- if image is unavailable show logo -->
                      <img
                        :class="{ 'z-index' : item.image == ''}"
                        alt="TTT logo"
                        src="../../../assets/img/src/logos/monogram.svg"
                        width="94"
                        height="94"
                      >
                    </div>
                  </a>

                  <div class="card__details">
                    <a :id="item.objectID" type="page"><p
                      class="card__details--name link-unstyle text-dark"
                    >{{ item.name }}</p></a>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div class="text-center">
            <app-link id="40" type="page" class="btn-secondary-dark-sm">
              See more experiences
            </app-link>
          </div>
        </div>
      </ais-hits>
    </ais-index>

    <!-- Discover more -->
    <ais-index
      v-show="counts.cmspages > 0"
      :index-name="indexPrefix + '_CMSPages'"
      class="hide-discover"
    >
      <ais-configure
        :analytics="true"
        filters="thinkExperience:false"
        attributesToRetrieve="objectID,name,image"
      />
      <algolia-result-counter
        index-name="cmspages"
        @nbHitsChange="countUpdate"
      />
      <ais-hits>
        <div
          v-if="items.length > 0"
          slot-scope="{ items }"
          class="searchResults--section hide-discover"
        >
          <div>
            <h3>Discover more</h3>
            <ul>
              <li
                v-for="(item, index) in items.slice(0, 2)"
                :key="item.objectID"
                class="discover-card"
                :class="{ last: index == items.length - 2 }"
                @click="
                  insightsClick({
                    queryID: item.__queryID,
                    indexName: indexPrefix + '_CMSPages',
                    objectID: item.objectID,
                    position: item.__position,
                  })
                "
              >
                <a :id="item.objectID" type="page">
                  <div
                    v-if="item.image"
                    class="card__image discover"
                    style="background-size: cover"
                    :style="{
                      backgroundImage: `url(${cleanImageUrl(item.image)})`,
                    }"
                  >
                    <!-- if image is unavailable show logo -->
                    <img
                      :class="{ 'z-index' : item.image == ''}"
                      alt="TTT logo"
                      src="../../../assets/img/src/logos/monogram.svg"
                      width="94"
                      height="94"
                    >
                  </div>
                </a>

                <p>
                  <a :id="item.objectID" type="page" class="card__details--name link-unstyle text-dark">{{
                    item.name
                  }}</a>
                </p>
              </li>
            </ul>
          </div>
        </div>
      </ais-hits>
    </ais-index>
  </ais-instant-search>
</template>

<script>
import algoliasearch from 'algoliasearch/lite';
import { _ } from 'vue-underscore';
import AlgoliaResultCounter from './AlgoliaResultCounter.vue';
import closeTab from '../../../mixins/closeTab';

export default {
  components: {
    AlgoliaResultCounter,
  },
  mixins: [closeTab],
  props: {
    query: { type: String, default: '' },
  },
  data() {
    return {
      searchClient: algoliasearch(
        process.env.VUE_APP_ALGOLIA_APPLICATIONID,
        process.env.VUE_APP_ALGOLIA_SEARCHKEY,
      ),
      indexPrefix: process.env.VUE_APP_ALGOLIA_ENV,
      counts: {
        villas: 0,
        collections: 0,
        experiences: 0,
        cmspages: 0,
      },
    };
  },
  computed: {
    currentSection() {
      return this.$store.getters['common/getCurrentSection'];
    },
    isCurrentSectionMobile() {
      return (
        this.$store.getters['common/getCurrentSection'] === 'Menumobile' ||
        false
      );
    },
    isMobile() {
      return this.$store.getters['common/getScreenType'] === 'mobile' || false;
    },
    screenType() {
      return this.$store.getters['common/getScreenType'];
    },
    isMobileMenuActive() {
      return this.currentSection === this.mobile.sections.start.menu.name;
    },
    isNavfixed() {
      return this.$store.getters['common/getNavfixed'];
    },
  },
  watch: {
    // when the query change then send a GA virtual page view
    query: _.debounce((newQuery, oldQuery) => {
      window.ga('send', 'pageview', `/?algolia=${newQuery}`);
    }, 2000),
  },
  methods: {
    countUpdate(indexName, count) {
      switch (indexName) {
        case 'villas':
          this.counts.villas = count;
          break;
        case 'collections':
          this.counts.collections = count;
          break;
        case 'experiences':
          this.counts.experiences = count;
          break;
        case 'cmspages':
          this.counts.cmspages = count;
          break;
        default:
          break;
      }
    },
    cleanImageUrl(url) {
      if (url) {
        url = url.replace(/\(/g, "%28"); // eslint-disable-line
        url = url.replace(/\)/g, "%29"); // eslint-disable-line
        url = url.replace(/\ /g, "%20"); // eslint-disable-line
      }
      return url;
    },
    insightsClick({
      queryID, indexName, position, objectID,
    }) {
      this.closeTab();
      this.$store
        .dispatch('algolia/registerClick', {
          index: indexName,
          // eslint-disable-next-line
          queryID: queryID,
          objectIDs: [objectID],
          positions: [position],
        })
        .then((res) => {
          let type;
          if (indexName.indexOf('_Villas') > -1) {
            type = 'villa';
          } else if (indexName.indexOf('_Collections') > -1) {
            type = 'collection';
          } else if (indexName.indexOf('_CMSPages') > -1) {
            type = 'page';
            if (objectID.indexOf('_blog') > -1) {
              type = 'blog';
              objectID = objectID.substring(0, objectID.indexOf('_blog'));
            }
          }
          this.$router.push({
            path: this.$store.getters['applinks/getLink'](type, objectID).url,
          });
        });
    },
    insightsConversion({ objectID }) {
      window.aa('conversion', {
        objectID,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/styles/settings/_config.scss";

.searchResults {
  padding-top: 1em;
  &--section {
    padding: 0 1em;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    color: $darkBlack;
    font-size: 14px;
    @media (min-width: 768px) and (max-width: 1025px) {
      max-width: 100%;
      margin-bottom: 4em;
    }
    @media (max-width: 768px) {
      margin-bottom: 2em;
    }
    h3 {
      font-size: 14px;
    }
    ul {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 10px;
      @media (max-width: 371px) {
        display: block;
      }
    }

    .btn-secondary-dark-sm {
      width: fit-content;
      margin: 1.5em 0 10px;
    }
  }
}

.card {
  cursor: pointer;
  margin-bottom: 1em;
  display: flex;
  flex-direction: column;
  background: $stoneCream;
  border: 1px solid $stoneLight;
  height: 100%;
  &.last {
    margin-bottom: 0;
  }
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
  &.villa--card {
    margin-bottom: 1em;
    &.last {
      margin-bottom: 0;
    }
  }
  &__image {
    height: 160px;
    width: 100%;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    margin-bottom: 5px;

    &.villa {
      height: 160px;
    }

    img {
      height: 100%;
      width: 100%;
      // to keep the fallback image behind background image
      position: relative;
      z-index: -1;
    }
  }
  &__details {
    padding: 0.5em;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    p {
      margin: 0;
    }
    &--name {
      margin-bottom: 8px !important;
    }
    &--region {
      font-size: 10px;
      text-transform: uppercase;
    }
    &--stats {
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      justify-content: space-evenly;
      p {
        font-size: 10px;
        // margin-right: 15px;
        display: flex;
        text-align: center;
        text-transform: uppercase;
      }
    }
  }
}

.discover-card {
  cursor: pointer;
  padding-bottom: 1.5em;
  border-left: 1px solid #dcdcdc;
  padding-left: 2.3em;
  @media (max-width: 1025px) {
    border: 0;
    padding: 0;
  }
  p {
    max-width: 300px;
  }
  &.last {
    margin-bottom: 0px;
    padding-bottom: 0px;
  }
}

.hide-discover {
  h3 {
    padding-left: 2.5em;

    @media (max-width: 768px) {
      padding-left: 0;
    }
  }
  @media (min-width: 1024px) and (max-width: 1286px) {
    display: none;
  }
}

.noResults {
  ul {
    display: block;
    li {
      line-height: 1.8;
    }
  }
}

.z-index {
  z-index: 0 !important;
}
</style>
